import { Editor, Transforms, Range, Point, Node, Path } from 'slate';
import { ReactEditor } from 'slate-react';
import shortid from 'shortid';

const withUtils = editor => {
	const {
		isVoid,
		isInline,
		deleteBackward,
	} = editor;

	// Void elements
	const VOIDS = ['image', 'video', 'audio', 'download', 'youtube', 'formula', 'spellcheck', 'quiz-void'];

	// Inline elements
	const INLINES = ['link', 'formula', 'spellcheck'];

	// Check if element is void
	// @element: object | current element
	editor.isVoid = element => {
		return VOIDS.includes(element.type) ? true : isVoid(element);
	};

	// Check if element is inline
	// @element: object | current element
	editor.isInline = element => {
		return INLINES.includes(element.type) ? true : isInline(element);
	};

	// Check if editor's selection is collapsed
	editor.isCollapsed = () => {
		return editor?.selection && Range.isCollapsed(editor?.selection);
	};

	// Check if element is active
	// @element: string | element's type
	editor.isElementActive = types => {
		const [match] = Editor.nodes(editor, {
			match: node => types instanceof Array
				? !Editor.isEditor(node) && types.includes(node.type)
				: !Editor.isEditor(node) && types === node.type
		}) ?? [];
		return !!match;
	};

	// Returns selection's current element
	// @element: string | element's type
	editor.getCurrentElement = type => {
		const { path } = editor.selection?.anchor ?? {};

		// Return null if there is no selection
		if (!path) return null;

		// Return current element
		return Editor.above(editor, {
			at: path,
			mode: 'lowest',
			match: node => node.type === type
		});
	};

	// Returns selection's previous element
	// @element: string | element's type
	editor.getPreviousElement = type => {
		const [, path] = editor.getCurrentElement(type) ?? [];

		// Return null if there is no cell
		if (!path) return null;

		try {
			// Try to get previous element
			// Slate throws error if there is no previous element
			const previousPath = Path.previous(path);

			// Return previous node's element and path
			return [
				Node.get(editor, previousPath),
				previousPath
			];
		} catch {
			return null;
		}
	};

	// Returns selection's next element
	// @element: string | element's type
	editor.getNextElement = type => {
		const [, path] = editor.getCurrentElement(type) ?? [];

		// Return null if there is no path
		if (!path) return null;

		try {
			// Try to get previous element
			// Slate throws error if there is no next element
			const nextPath = Path.next(path);

			// Return next node's element and path
			return [
				Node.get(editor, nextPath),
				nextPath
			];
		} catch {
			return null;
		}
	};

	// Handle's deletion event
	// @args: default arguments
	editor.deleteBackward = (args) => {
		// Define start point
		const start = {
			path: [0, 0],
			offset: 0,
		};

		// If cursor is at the begining of document
		// Reset element type
		if (Point.equals(editor.selection.anchor, start)) {
			Transforms.setNodes(editor, { type: 'paragraph' });
		}

		// Proceed with normal deletion
		deleteBackward(args);
	};

	// Toggle given element on selection
	editor.toggleElement = type => {
		Transforms.setNodes(editor, {
			type: editor.isElementActive(type)
				? 'paragraph'
				: type
		});

		// Refocus after change
		ReactEditor.focus(editor);
	};

	// Check if there are no undo ops
	editor.isUndoDisabled = () => {
		return !editor?.history?.undos?.length;
	};

	// Check if there are no redo ops
	editor.isRedoDisabled = () => {
		return !editor?.history?.redos?.length;
	};

	// Gather data from selection
	editor.prepareYoutube = () => {
		// Data preparation
		return {
			path: editor?.selection
		};
	};

	// Insert youtube in selection
	editor.insertYoutube = (data) => {
		// Focus editor before inserting
		ReactEditor.focus(editor);

		// Select old path, because selection
		// was lost on youtube modal inputs
		Transforms.select(editor, data.path);

		// Check if youtube is active
		const isActive = editor.isElementActive('youtube');

		// Create youtube element
		const youtube = {
			type: 'youtube',
			href: data.href,
			children: [{
				text: ''
			}]
		};

		// Remove youtube node
		if (isActive) Transforms.removeNodes(editor, {
			match: node => node.type === 'youtube'
		});

		// Inserts youtube in selection
		Transforms.insertNodes(editor, youtube);

		// Collapse selection
		Transforms.collapse(editor, { edge: 'end' });
	};

	// Removes youtube from selection
	editor.removeYoutube = (data) => {
		// Unwrap youtube format
		Transforms.unwrapNodes(editor, {
			match: node => node.type === 'youtube',
			at: data.path
		});

		// Refocus on editor
		ReactEditor.focus(editor);
	};

	return editor;
};

export default withUtils;
